import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errorMessage } from "../../api";
import type { AdminBinding, Connection } from "../../api/types";
import { ConnectorBadge, ErrorBox, Spinner } from "../../components/bits";
import { Icon } from "../../components/icons";
import { formatDateTime } from "../../lib/format";
import { loadCollection, syncReportText, toast, toastError } from "../../store/app";
import { useActorMap, useApp } from "../../store/hooks";
import { ConfirmDialog, Forbidden, useOrgData } from "./shared";

const HEALTH_TEXT: Record<Connection["health"], string> = {
  ok: "Healthy",
  error: "Error",
  needs_auth: "Needs sign-in",
  unknown: "Unknown",
};

function bindingProblem(b: AdminBinding): boolean {
  return !!b.last_error || b.connection_health === "error" || b.connection_health === "needs_auth";
}

export function SyncTab() {
  const orgId = useApp((s) => s.orgId);
  const actors = useActorMap();
  const [params, setParams] = useSearchParams();
  const problemsOnly = params.get("problems") === "1";
  const bindings = useOrgData((id) => api.admin.bindings(id));
  const [connections, setConnections] = useState<Connection[] | null>(null);
  const [connError, setConnError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Connection | null>(null);

  const loadConnections = () => {
    if (!orgId) return;
    setConnError(null);
    api
      .connections(orgId, true)
      .then((r) => setConnections(r.connections))
      .catch((e) => setConnError(errorMessage(e)));
  };
  useEffect(loadConnections, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const shownBindings = useMemo(
    () => (bindings.data?.bindings ?? []).filter((b) => !problemsOnly || bindingProblem(b)),
    [bindings.data, problemsOnly],
  );
  const shownConnections = useMemo(
    () => (connections ?? []).filter((c) => !problemsOnly || c.health === "error" || c.health === "needs_auth"),
    [connections, problemsOnly],
  );

  if (bindings.forbidden) return <Forbidden />;

  return (
    <div className="admin-sync">
      <div className="section-head">
        <label className="check-row">
          <input
            type="checkbox"
            checked={problemsOnly}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.checked) next.set("problems", "1");
              else next.delete("problems");
              setParams(next, { replace: true });
            }}
          />
          Only show problems
        </label>
        <button
          type="button"
          className="btn small"
          onClick={() => {
            bindings.reload();
            loadConnections();
          }}
        >
          <Icon name="refresh" size={14} />
          Refresh
        </button>
      </div>

      <section aria-labelledby="bindings-title" className="admin-section">
        <h2 id="bindings-title">Sync bindings</h2>
        {bindings.error && <ErrorBox error={bindings.error} onRetry={bindings.reload} />}
        {!bindings.data && !bindings.error ? (
          <Spinner />
        ) : shownBindings.length === 0 ? (
          <p className="muted small">{problemsOnly ? "No failing bindings." : "No collections are synced yet."}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Collection</th>
                  <th>Remote</th>
                  <th>Connection</th>
                  <th>Mode</th>
                  <th>Health</th>
                  <th>Last sync</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shownBindings.map((b) => (
                  <tr key={b.id} className={bindingProblem(b) ? "problem" : ""}>
                    <td>
                      <Link to={`/c/${b.collection_id}`}>{b.collection_name}</Link>
                    </td>
                    <td>
                      <ConnectorBadge type={b.connector_type} /> {b.remote_collection_name}
                    </td>
                    <td>
                      {b.connection_name}
                      <div className="muted small">{b.connection_scope === "org" ? "organization" : "personal"}</div>
                    </td>
                    <td className="small">{b.mode.replace(/_/g, " ")}</td>
                    <td>
                      <span className={`health ${b.last_error ? "error" : b.connection_health}`}>
                        <span className="dot" aria-hidden="true" />
                        {b.last_error ? "Sync error" : HEALTH_TEXT[b.connection_health]}
                      </span>
                      {b.last_error && <div className="form-error small">{b.last_error}</div>}
                    </td>
                    <td className="small">{b.last_synced_at ? formatDateTime(b.last_synced_at) : "never"}</td>
                    <td>
                      <button
                        type="button"
                        className="btn small"
                        disabled={busy === b.id}
                        onClick={async () => {
                          setBusy(b.id);
                          try {
                            const report = await api.syncBinding(b.id);
                            toast(syncReportText(report), report.errors.length ? "error" : "success", 7000);
                            bindings.reload();
                            void loadCollection(b.collection_id).catch(() => undefined);
                          } catch (e) {
                            toastError(e, "Sync failed");
                          } finally {
                            setBusy(null);
                          }
                        }}
                      >
                        {busy === b.id ? "Syncing…" : "Sync now"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="aconn-title" className="admin-section">
        <h2 id="aconn-title">All connections</h2>
        <p className="muted small">Organization connections and every member's personal connections. Credentials are never shown.</p>
        {connError && <ErrorBox error={connError} onRetry={loadConnections} />}
        {connections === null && !connError ? (
          <Spinner />
        ) : shownConnections.length === 0 ? (
          <p className="muted small">{problemsOnly ? "No connection problems." : "No connections."}</p>
        ) : (
          <ul className="conn-list">
            {shownConnections.map((c) => (
              <li key={c.id} className="conn-card">
                <div className="conn-main">
                  <div className="conn-title">
                    <ConnectorBadge type={c.connector_type} />
                    <strong>{c.name}</strong>
                    <span className={`health ${c.health}`}>
                      <span className="dot" aria-hidden="true" />
                      {HEALTH_TEXT[c.health]}
                    </span>
                  </div>
                  <div className="muted small">
                    {c.scope === "org" ? "Organization" : "Personal"} · owner{" "}
                    {actors.get(c.owner_actor_id)?.display_name ?? "unknown"}
                    {c.remote_tenant && ` · ${c.remote_tenant}`}
                  </div>
                  {c.last_error && <div className="form-error small">{c.last_error}</div>}
                </div>
                <div className="conn-actions">
                  <button
                    type="button"
                    className="btn small"
                    disabled={busy === c.id}
                    onClick={async () => {
                      setBusy(c.id);
                      try {
                        const updated = await api.testConnection(c.id);
                        setConnections((cs) => cs?.map((x) => (x.id === c.id ? updated : x)) ?? null);
                        toast(
                          updated.health === "ok" ? "Connection OK" : `Connection: ${HEALTH_TEXT[updated.health]}`,
                          updated.health === "ok" ? "success" : "error",
                        );
                      } catch (e) {
                        toastError(e, "Test failed");
                      } finally {
                        setBusy(null);
                      }
                    }}
                  >
                    Test
                  </button>
                  <button type="button" className="btn small danger" onClick={() => setDeleting(c)}>
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {deleting && (
        <ConfirmDialog
          title={`Delete connection “${deleting.name}”?`}
          confirmLabel="Delete connection"
          danger
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            try {
              await api.deleteConnection(deleting.id);
              setConnections((cs) => cs?.filter((x) => x.id !== deleting.id) ?? null);
              setDeleting(null);
              bindings.reload();
            } catch (e) {
              toastError(e, "Delete failed");
            }
          }}
        >
          <p>Its sync bindings are removed. Local items stay.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
